import { z } from 'zod';

/**
 * Рантайм-конфигурация приложения (CORE-39). Загружается из `/config.json`
 * ДО `bootstrapApplication` — этот файл рендерится при старте контейнера
 * (docker-entrypoint.d, jq подставляет адрес провайдера), поэтому один бандл
 * обслуживает все окружения (переезд URL = рестарт, не пересборка).
 *
 * Путь `/config.json` в корне (не `/assets/`): ассеты проекта раздаются из
 * `public/` прямо в корень статики (там же `silent-renew.html`,
 * `favicon.ico`) — см. project.json assets glob.
 *
 * Валидация load-bearing: unset переменная даёт `''` (пустая строка), а не
 * падение. Без схемы это привело бы к тихому редиректу на собственный origin
 * приложения — далеко от причины. `z.url()` ловит это на старте (fail-fast).
 *
 * `clientId` переехал сюда из `environment.ts` вместе с миграцией на Authelia
 * (ADR-026): клиенты провайдера задаются его конфигурацией на среду, и
 * держать их имя compile-time значило бы пересобирать бандл ради переезда.
 * Понятия realm у Authelia нет вовсе — ключ исчез.
 */
const RuntimeConfigSchema = z.object({
  auth: z.object({
    /** Корневой URL провайдера. Он же `iss` в токене — Envoy сверяет его байт в байт. */
    issuer: z.url(),
    clientId: z.string().min(1),
  }),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

/**
 * Заполняется {@link loadRuntimeConfig} в `main.ts` ДО того, как вычислится
 * `appConfig`. Приватная (не `export`): единственный доступ — через
 * {@link getRuntimeConfig}, который бросает внятную ошибку при обращении до
 * загрузки, вместо тихого `Cannot read properties of undefined`.
 */
let loaded: RuntimeConfig | undefined;

/**
 * Возвращает загруженный конфиг. Бросает, если вызван ДО `loadRuntimeConfig()`
 * — страховка от временного сцепления: `appConfig` читает адрес провайдера в
 * eager-контексте (при построении литерала providers), и корректность держится
 * на том, что `main.ts` импортирует `app.config` ДИНАМИЧЕСКИ уже после загрузки
 * конфига. Если будущий статический импорт `app.config` нарушит этот порядок,
 * здесь будет явная ошибка с причиной, а не `undefined`-разыменование вглубине.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!loaded) {
    throw new Error(
      'runtimeConfig прочитан до loadRuntimeConfig(): app.config.ts должен ' +
        'импортироваться динамически ПОСЛЕ await loadRuntimeConfig() в main.ts',
    );
  }
  return loaded;
}

/**
 * Загружает и валидирует `/config.json` (корень статики, не `/assets/`).
 * Бросает при отсутствии файла, невалидном JSON или непрошедшей схеме —
 * вызывающая сторона (`main.ts`) ловит и показывает hard error page, не
 * бутстрапя приложение «наполовину».
 */
export async function loadRuntimeConfig(): Promise<void> {
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(
      `Runtime config fetch failed: ${res.status} ${res.statusText}`,
    );
  }

  // Guard от SPA-fallback: если config.json не задеплоен, хост без явного
  // `location = /config.json` (dev-сервер, чужой статик-хост) отдаёт index.html
  // с HTTP 200. Тогда res.ok=true, но res.json() падает на HTML с невнятным
  // SyntaxError. Проверяем content-type, чтобы ошибка называла реальную причину.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      `Runtime config: ожидался JSON, получен "${contentType}" — ` +
        `вероятно /config.json не задеплоен и сработал SPA-fallback на index.html`,
    );
  }

  loaded = Object.freeze(RuntimeConfigSchema.parse(await res.json()));
}
